import {
  PropsWithChildren,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type CSSProperties
} from "react";
import LitDialog, { Ref } from "./LitDialog";
import ModalHeader, { type ModalHeaderConfig } from "./ModalHeader";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { isOverlayEscapeKeyActive, useOverlayDismiss } from "@/hooks/useOverlayDismiss";
import SideSheetModal from "@/components/SideSheetModal";
import ModalContext from "@/hooks/useModalContext";
import {
  lockDocumentBodyScroll,
  resetDocumentBodyScrollLockForTests,
  unlockDocumentBodyScroll
} from "@/lib/documentBodyOverflow";

export function resetDialogBodyScrollLockForTests() {
  resetDocumentBodyScrollLockForTests();
}

export type { Ref };
export type { ModalHeaderConfig as DialogHeader };

export type Props = PropsWithChildren<{
  headline?: ReactNode;
  header?: ModalHeaderConfig;
  open?: boolean;
  onClosed?(): void;
  actions: ReactNode;
  className?: string;
  displayOverflow?: boolean;
  hasFormElements?: boolean;
  sheetZIndex?: number;
  fixedContent?: ReactNode;
  fixedContentCompact?: boolean;
  dismissOnOutsideClick?: boolean;
  disableClose?: boolean;
  closeButtonClassName?: string;
}>;

export const dialogContainerStyle = {
  "--md-dialog-container-color": "var(--md-sys-color-surface-container-low)"
} as CSSProperties;

const getCustomStyles = (dismissOnOutsideClick: boolean) => `
  ${!dismissOnOutsideClick ? `::backdrop { pointer-events: none; }` : ""}
  .scrim { 
    z-index: 50;
  }
`;

const Dialog = forwardRef<Ref, Props>(
  (
    {
      headline,
      header,
      actions,
      children,
      className,
      displayOverflow,
      open,
      onClosed,
      hasFormElements,
      sheetZIndex,
      fixedContent,
      fixedContentCompact = false,
      dismissOnOutsideClick = true,
      disableClose = false,
      closeButtonClassName = "!my-1",
      ...props
    }: Props,
    ref
  ) => {
    const { isCompactLayout } = useIsCompactLayout();
    const litDialogRef = useRef<Ref>(null);
    const closeLocked = header?.disableClose ?? disableClose;
    const allowOutsideDismiss = dismissOnOutsideClick && !closeLocked;
    const isCompactSheet = isCompactLayout && !!hasFormElements;
    const resolvedHeader: ModalHeaderConfig = {
      title: header?.title ?? headline ?? "",
      onBack: header?.onBack,
      action: header?.action,
      disableClose: closeLocked
    };

    const closeDialog = useCallback(() => {
      if (closeLocked) return;
      if (litDialogRef.current) {
        void litDialogRef.current.close();
        return;
      }
      onClosed?.();
    }, [closeLocked, onClosed]);

    useOverlayDismiss({
      open: !!open && !isCompactSheet,
      disableClose: closeLocked,
      dismiss: closeDialog
    });

    const handleCancel = (event: Event) => {
      if (closeLocked || isOverlayEscapeKeyActive()) {
        event.preventDefault();
        return;
      }
      if (!allowOutsideDismiss) event.preventDefault();
    };

    useImperativeHandle(ref, () => {
      if (isCompactLayout && hasFormElements) {
        return {
          close: async () => {
            if (onClosed) {
              onClosed();
            }
          },
          show: async () => {}
        } as Ref;
      }
      return litDialogRef.current as Ref;
    }, [isCompactLayout, hasFormElements, onClosed]);

    useEffect(() => {
      if (!open || typeof document === "undefined") {
        return;
      }

      lockDocumentBodyScroll();

      // Add this to ensure dialog content is scrollable on iOS
      const dialogElements = document.querySelectorAll("md-dialog");
      dialogElements.forEach((dialog) => {
        const dialogContainer = dialog.shadowRoot?.querySelector(".container") as HTMLElement;

        if (dialogContainer) {
          dialogContainer.style.position = "relative";
          (dialogContainer.style as any)["-webkit-overflow-scrolling"] = "touch";
        }
      });

      return () => {
        unlockDocumentBodyScroll();
      };
    }, [open]);

    // Temporary solution needed to override the overflow hidden on the dialog
    useEffect(() => {
      if (displayOverflow && typeof document !== "undefined") {
        setTimeout(showOverflow, 100);
      }
    }, [displayOverflow]);

    function showOverflow() {
      if (typeof document === "undefined") return;

      const dialogElements = document.querySelectorAll("md-dialog");
      dialogElements.forEach((dialog) => {
        const dialogContainer = dialog.shadowRoot?.querySelector(".container") as HTMLElement;
        const scrollerElement = dialogContainer?.querySelector(".scroller") as HTMLElement;

        if (dialogContainer) {
          dialogContainer.style.overflow = "visible";
        }
        if (scrollerElement) {
          scrollerElement.style.overflow = "visible";
        }
      });
    }
    useEffect(() => {
      setTimeout(() => {
        if (typeof document !== "undefined") {
          const dialogElements = document.querySelectorAll("md-dialog");

          dialogElements.forEach((dialog) => {
            if (dialog.shadowRoot) {
              var sheet = new CSSStyleSheet();
              sheet.replaceSync(getCustomStyles(allowOutsideDismiss));
              dialog.shadowRoot.adoptedStyleSheets.push(sheet);
            }
          });
        }
      }, 50); // Short delay to ensure shadow DOM contents are rendered
    }, [allowOutsideDismiss]);

    return isCompactLayout && hasFormElements ? (
      <SideSheetModal
        isSidebarOpen={!!open}
        setSidebarOpen={(isOpen) => {
          if (!isOpen && onClosed) {
            onClosed();
          }
        }}
        header={resolvedHeader}
        actions={actions}
        fixedContent={fixedContentCompact ? fixedContent : undefined}
        disableFocusManagement
        dismissOnOutsideClick={allowOutsideDismiss}
        zIndex={sheetZIndex}
      >
        <ModalContext.Provider value={true}>
          <div className="w-full min-w-0" data-testid="dialog-compact-sheet-body">
            {children}
            {!fixedContentCompact && fixedContent && <div>{fixedContent}</div>}
          </div>
        </ModalContext.Provider>
      </SideSheetModal>
    ) : (
      <LitDialog
        onClosed={onClosed}
        onCancel={handleCancel}
        open={open}
        {...props}
        ref={litDialogRef}
        className={className}
        style={dialogContainerStyle}
        data-dismiss-on-outside-click={allowOutsideDismiss ? "true" : "false"}
      >
        <ModalHeader
          slot="headline"
          title={resolvedHeader.title}
          onBack={resolvedHeader.onBack}
          action={resolvedHeader.action}
          onClose={closeDialog}
          disableClose={resolvedHeader.disableClose}
          titleClassName="font-serif text-title-large-bold"
          backButtonClassName="!my-1 -ml-2.5"
          closeButtonClassName={closeButtonClassName}
        />
        <ModalContext.Provider value={true}>
          <div slot="content" className="!pb-6">
            {children}
          </div>
        </ModalContext.Provider>
        <div slot="actions">
          <div className="flex flex-col w-full gap-2">
            {fixedContent}
            <div className="flex flex-wrap justify-end gap-2">{actions}</div>
          </div>
        </div>
      </LitDialog>
    );
  }
);

Dialog.displayName = "Dialog";

export default Dialog;
